import { useRef, useEffect } from "react";

export const EXERCISES = [
  {
    name: "Flexão do Tronco",
    joints: [
      [12, 24, 28], // Lado Direito
      [11, 23, 27], // Lado Esquerdo
    ],
    resultMode: "single",
    instructions:
      "\n 1. POSICIONE-SE com a PERNA RETA.\n2. INICIE a avaliação para fixar a postura.\n3. EXECUTE a inclinação para frente.",
    validation: {
      positionCheck: (landmarks) =>
        landmarks[24].y > landmarks[12].y || landmarks[23].y > landmarks[11].y,
      kneesToCheck: ["right", "left"],
    },
  },
  {
    name: "Alongamento do Quadril",
    joints: [
      [12, 24, 26],
      [11, 23, 25],
    ],
    resultMode: "multi",
    instructions:
      "\n 1. DEITE-SE com o CORPO RETO.\n2. INICIE a avaliação.\n3. ELEVE a perna direita esticada, depois a esquerda.",
    validation: {
      positionCheck: () => true,
      kneesToCheck: ["right", "left"],
    },
  },
];

export function BodyVisualizer({ pose, angleData, angleValue, width, height }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!pose || !angleData || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    }

    ctx.clearRect(0, 0, width, height);

    const colors = {
      fill: ["#C5C5C5", "#EAEAEA"],
      stroke: "#888888",
      highlight: "rgba(0, 99, 255, 0.3)",
      text: "#002A6E",
    };
    const landmarks = pose;
    const lm = (index) => landmarks[index];
    const allLandmarks = Object.values(landmarks).filter(
      (lm) => lm && lm.visibility > 0.1
    );
    if (allLandmarks.length === 0) return;
    let minX = 1,
      maxX = 0,
      minY = 1,
      maxY = 0;
    allLandmarks.forEach((lm) => {
      minX = Math.min(minX, lm.x);
      maxX = Math.max(maxX, lm.x);
      minY = Math.min(minY, lm.y);
      maxY = Math.max(maxY, lm.y);
    });
    const poseWidth = maxX - minX;
    const poseHeight = maxY - minY;
    const scale = Math.min(
      (width * 0.75) / poseWidth,
      (height * 0.75) / poseHeight
    );
    const offsetX = (width - poseWidth * scale) / 2 - minX * scale;
    const offsetY = (height - poseHeight * scale) / 2 - minY * scale;
    const t = (p) =>
      p ? { x: p.x * scale + offsetX, y: p.y * scale + offsetY } : null;
    const limbWidth = width / 14;
    const contourWidth = 2;
    const drawLineSegment = (p1, p2, thickness, color) => {
      if (!p1 || !p2) return;
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.lineWidth = thickness;
      ctx.strokeStyle = color;
      ctx.lineCap = "round";
      ctx.stroke();
    };
    const drawBodyLayer = (thickness, color) => {
      drawLineSegment(t(lm(11)), t(lm(12)), thickness, color);
      drawLineSegment(t(lm(23)), t(lm(24)), thickness, color);
      drawLineSegment(t(lm(11)), t(lm(23)), thickness, color);
      drawLineSegment(t(lm(12)), t(lm(24)), thickness, color);
      drawLineSegment(t(lm(0)), t(lm(11)), thickness, color);
      drawLineSegment(t(lm(0)), t(lm(12)), thickness, color);
      drawLineSegment(t(lm(11)), t(lm(13)), thickness, color);
      drawLineSegment(t(lm(13)), t(lm(15)), thickness, color);
      drawLineSegment(t(lm(12)), t(lm(14)), thickness, color);
      drawLineSegment(t(lm(14)), t(lm(16)), thickness, color);
      drawLineSegment(t(lm(23)), t(lm(25)), thickness, color);
      drawLineSegment(t(lm(25)), t(lm(27)), thickness, color);
      drawLineSegment(t(lm(24)), t(lm(26)), thickness, color);
      drawLineSegment(t(lm(26)), t(lm(28)), thickness, color);
    };
    drawBodyLayer(limbWidth + contourWidth, colors.stroke);
    const gradient = ctx.createLinearGradient(
      0,
      offsetY,
      0,
      offsetY + poseHeight * scale
    );
    gradient.addColorStop(0, colors.fill[0]);
    gradient.addColorStop(1, colors.fill[1]);
    drawBodyLayer(limbWidth, gradient);
    const nose = t(lm(0));
    if (nose) {
      const headRadius = width / 8;
      ctx.beginPath();
      ctx.ellipse(
        nose.x,
        nose.y,
        headRadius * 0.8,
        headRadius,
        0,
        0,
        2 * Math.PI
      );
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.strokeStyle = colors.stroke;
      ctx.lineWidth = contourWidth / 2;
      ctx.stroke();
    }
    const [p1_idx, p2_idx, p3_idx] = angleData;
    const p1 = t(lm(p1_idx)),
      p2 = t(lm(p2_idx)),
      p3 = t(lm(p3_idx));
    if (p1 && p2 && p3) {
      ctx.fillStyle = colors.highlight;
      ctx.beginPath();
      ctx.moveTo(p2.x, p2.y);
      const startAngle = Math.atan2(p1.y - p2.y, p1.x - p2.x);
      const endAngle = Math.atan2(p3.y - p2.y, p3.x - p2.x);
      ctx.arc(p2.x, p2.y, width / 4, startAngle, endAngle);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = colors.text;
      ctx.font = `bold ${width / 8}px 'Segoe UI', sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const radius = width / 3.5;
      const midAngle = startAngle + (endAngle - startAngle) / 2;
      const isReversed = Math.abs(startAngle - endAngle) > Math.PI;
      const finalAngle = isReversed ? midAngle + Math.PI : midAngle;
      const textX = p2.x + radius * Math.cos(finalAngle);
      const textY = p2.y + radius * Math.sin(finalAngle);
      ctx.fillText(`${angleValue}°`, textX, textY);
    }
  }, [pose, angleData, angleValue, width, height]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: "white",
        border: "1px solid #ddd",
      }}
    />
  );
}
